import { useEffect, useState, useCallback } from "react";

type Theme = "light" | "dark";
export type ThemePref = "light" | "dark" | "system";

const STORAGE_KEY = "theme-v2";
const PREF_KEY = "theme-pref-v1";

function getSystemTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function getInitialPref(): ThemePref {
  if (typeof window === "undefined") return "dark";
  const stored = localStorage.getItem(PREF_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") return stored;
  const legacy = localStorage.getItem(STORAGE_KEY);
  if (legacy === "light" || legacy === "dark") return legacy;
  return "dark";
}

function resolveTheme(pref: ThemePref): Theme {
  return pref === "system" ? getSystemTheme() : pref;
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === "light") root.classList.add("light");
  else root.classList.remove("light");
}

export function useTheme() {
  const [pref, setPrefState] = useState<ThemePref>(getInitialPref);
  const [theme, setThemeState] = useState<Theme>(() => resolveTheme(getInitialPref()));

  useEffect(() => {
    const resolved = resolveTheme(pref);
    setThemeState(resolved);
    applyTheme(resolved);
    localStorage.setItem(PREF_KEY, pref);
    localStorage.setItem(STORAGE_KEY, resolved);

    if (pref === "system" && typeof window !== "undefined") {
      const mq = window.matchMedia("(prefers-color-scheme: light)");
      const handler = () => {
        const t = mq.matches ? "light" : "dark";
        setThemeState(t);
        applyTheme(t);
        localStorage.setItem(STORAGE_KEY, t);
      };
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
  }, [pref]);

  const toggleTheme = useCallback(() => {
    setPrefState((p) => (resolveTheme(p) === "dark" ? "light" : "dark"));
  }, []);

  const setTheme = useCallback((t: Theme) => setPrefState(t), []);
  const setThemePref = useCallback((p: ThemePref) => setPrefState(p), []);

  return { theme, themePref: pref, toggleTheme, setTheme, setThemePref };
}

if (typeof window !== "undefined") {
  applyTheme(resolveTheme(getInitialPref()));
}
